import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';

import { SingleSelect } from '@/common';
import { FilterModalLayout, FilterPopUpValues } from '@/components/layouts';
import { PAGE_SIZE } from '@/constants';
import { AuthContext, RolesAndPermissionContext } from '@/contexts';
import { getActiveStatusFilterOptions } from '@/helpers';
import { useGetAllOrganizations } from '@/services';
import { useFilterStore } from '@/store';
import { FilterUsersProps, Organization, RoleType, SelectOptionProps } from '@/types';

export const UsersFilters = ({
  onClose,
  reset,
  apply,
  filterByStatus,
  filterByRole,
  filterByOrganization
}: FilterUsersProps) => {
  const [t] = useTranslation('common');
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { organizationFilter } = useFilterStore();
  const { roles } = useContext(RolesAndPermissionContext);
  const [searchOrganization, setSearchOrganization] = useState<string>('');
  const {
    data: allOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(PAGE_SIZE, searchOrganization);
  const getOrganizationOptions = () => {
    return allOrganizations?.pages.flatMap((page) =>
      page.data?.items.map((organization: Organization) => ({
        value: organization.id,
        label: organization.name
      }))
    ) as SelectOptionProps[];
  };

  return (
    <Formik
      initialValues={{
        filterByStatus: filterByStatus ?? null,
        filterByRole: filterByRole ?? null,
        filterByOrganization: filterByOrganization ?? null
      }}
      onSubmit={() => undefined}>
      {({ setFieldValue, values }) => (
        <Form>
          <FilterModalLayout
            apply={apply as (values: FilterPopUpValues) => void}
            reset={reset}
            onClose={onClose}
            values={values}>
            <SingleSelect
              isClearable
              placeholder={t('selectStatus')}
              id='filterByStatus'
              label={t('status')}
              options={getActiveStatusFilterOptions()}
              handleChange={(value) => setFieldValue('filterByStatus', value as SelectOptionProps)}
              value={values.filterByStatus}
            />
            <SingleSelect
              isClearable
              placeholder={t('selectRole')}
              id='filterByRole'
              label={t('role')}
              options={roles.map((role: RoleType) => ({
                value: role.id,
                label: role.name
              }))}
              handleChange={(value) => setFieldValue('filterByRole', value as SelectOptionProps)}
              value={values.filterByRole}
            />
            {currentUser?.organizations.length === 1 || organizationFilter ? null : (
              <SingleSelect
                isClearable
                label={t('organization')}
                id='filterByOrganization'
                options={
                  currentUser?.organizations && currentUser.organizations.length >= 2
                    ? currentUser?.organizations?.map((orgs: Organization) => ({
                        value: orgs.id,
                        label: orgs.name
                      }))
                    : getOrganizationOptions()
                }
                handleChange={(value) =>
                  setFieldValue('filterByOrganization', value as SelectOptionProps)
                }
                value={values.filterByOrganization}
                placeholder={t('selectOrganization')}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isSearchable={true}
                handleInputChange={(inputValue: string) => setSearchOrganization(inputValue)}
              />
            )}
          </FilterModalLayout>
        </Form>
      )}
    </Formik>
  );
};
